<script lang="ts">
  import Icon from '../icons/Icon.svelte';
  import type { ComponentProps } from 'svelte';

  type IconName = ComponentProps<typeof Icon>['name'];

  const items: { n: string; icon: IconName; h: string; em: string; p: string }[] = [
    {
      n: '01',
      icon: 'search',
      h: 'Site',
      em: 'Detective',
      p: 'Paste a URL or trigger detection — VuAuth recognizes the site and surfaces the matching code at the top. The 6-step ritual collapses to one tap.'
    },
    {
      n: '02',
      icon: 'clock',
      h: 'Time',
      em: 'Crystal',
      p: 'A rotating energy ring shows the code lifespan at a glance. Below 8 seconds, the ghost of the next code materializes. No more surprise expirations.'
    },
    {
      n: '03',
      icon: 'send',
      h: 'Code',
      em: 'Teleport',
      p: 'Tap once and the code lives in a 15-second decay buffer at the bottom of the screen. Missed your paste window? Re-teleport. The race is over.'
    },
    {
      n: '04',
      icon: 'command',
      h: 'Cmd-Pal',
      em: 'Universal',
      p: '⌘K to open. Type to filter, type / for slash commands, paste an otpauth:// URI to add an account in a single keystroke. Mouse-free authentication.'
    },
    {
      n: '05',
      icon: 'fullscreen',
      h: 'Stage',
      em: 'Mode',
      p: '120-pixel digits, fullscreen. Optional voice readout via local SpeechSynthesis. Designed for typing into a second device across the room — no relay, no cloud.'
    },
    {
      n: '06',
      icon: 'eye',
      h: 'One-Glance',
      em: 'Reveal',
      p: 'Codes start hidden. Press for 250ms to reveal, tap to copy. Window blur or tab switch re-hides automatically. Shoulder-surf protection by default.'
    },
    {
      n: '07',
      icon: 'shield',
      h: 'Setup',
      em: 'Verifier+',
      p: 'Paste a secret and watch the HMAC math live: counter bytes, signature hex, dynamic-truncation offset, computed code. The math is visible; trust is verifiable.'
    },
    {
      n: '08',
      icon: 'broadcast',
      h: 'Beam',
      em: '',
      p: 'Move an account to your laptop without touching the network. Render a QR, scan it, done. Zero packets. Zero servers. Zero accounts.'
    },
    {
      n: '09',
      icon: 'pin',
      h: 'Spatial',
      em: 'Pin',
      p: 'Drag your most-used codes into a horizontal Quick Bar. Muscle memory replaces search. The accounts you actually use surface where your thumb already is.'
    },
    {
      n: '10',
      icon: 'info',
      h: 'Provenance',
      em: 'Card',
      p: 'Per-code dossier proving how it was generated. 0 packets sent, 0ms ago, HMAC signature in hex. Privacy as visible proof — not a footer claim.'
    }
  ];
</script>

<section id="innovations" class="section scroll-reveal">
  <div class="wrap">
    <div class="head">
      <div>
        <span class="eyebrow">10 innovations</span>
        <h2 class="h-section heading">
          Engineered <em>2030 ahead.</em>
        </h2>
      </div>
      <p class="lede">
        Every feature attacks one specific moment of friction. None require a server. None send a
        packet. None compromise the math.
      </p>
    </div>

    <div class="grid">
      {#each items as item (item.n)}
        <article class="card">
          <span class="num">{item.n}</span>
          <div class="icon"><Icon name={item.icon} size={20} /></div>
          <h3 class="title">
            {item.h}{#if item.em}
              <em>{item.em}</em>
            {/if}
          </h3>
          <p class="desc">{item.p}</p>
        </article>
      {/each}
    </div>
  </div>
</section>

<style>
  .section {
    padding: 96px 0;
    position: relative;
  }
  @media (max-width: 640px) {
    .section {
      padding: 64px 0;
    }
  }
  :global([data-theme='brutalist']) .section {
    border-bottom: 2px solid var(--text);
  }
  .wrap {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 0 var(--gutter);
  }
  @media (max-width: 640px) {
    .wrap {
      padding: 0 var(--gutter-sm);
    }
  }
  .head {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 32px;
    margin-bottom: 56px;
    flex-wrap: wrap;
  }
  .head .lede {
    max-width: 480px;
  }
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    padding: 6px 12px;
    border: 1px solid var(--border-2);
    border-radius: 999px;
    background: var(--bg-card);
  }
  :global([data-theme='brutalist']) .eyebrow {
    border-radius: 0;
    border: 2px solid var(--text);
    background: var(--accent-dim);
    color: var(--text);
    font-weight: 800;
  }
  .heading {
    margin-top: 18px;
  }
  .h-section {
    font-size: clamp(32px, 5vw, 56px);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1;
  }
  .h-section em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .h-section {
    font-weight: 900;
  }
  :global([data-theme='brutalist']) .h-section em {
    color: var(--text);
  }
  .lede {
    font-size: clamp(16px, 1.7vw, 20px);
    color: var(--text-2);
    line-height: 1.55;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
    background: var(--border);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    overflow: hidden;
  }
  :global([data-theme='brutalist']) .grid {
    background: var(--text);
    border: 2px solid var(--text);
    border-radius: 0;
    gap: 2px;
    box-shadow: 8px 8px 0 var(--text);
  }
  @media (max-width: 960px) {
    .grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 540px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
  .card {
    padding: 32px 28px;
    background: var(--bg-elev);
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: relative;
    transition: background var(--t);
    min-height: 220px;
  }
  .card:hover {
    background: var(--bg-card-hover);
  }
  :global([data-theme='brutalist']) .card {
    background: var(--bg);
  }
  :global([data-theme='brutalist']) .card:hover {
    background: var(--accent-dim);
  }
  .icon {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    background: var(--accent-dim);
    border-radius: var(--radius-sm);
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .icon {
    background: var(--text);
    color: var(--bg);
    border-radius: 0;
  }
  :global([data-theme='brutalist']) .card:hover .icon {
    background: var(--text);
    color: var(--accent-dim);
  }
  .num {
    position: absolute;
    top: 18px;
    right: 22px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-4);
    letter-spacing: 0.04em;
  }
  .title {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.015em;
  }
  :global([data-theme='brutalist']) .title {
    font-weight: 800;
  }
  .title em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .title em {
    color: var(--text);
  }
  .desc {
    font-size: 13px;
    color: var(--text-3);
    line-height: 1.55;
  }
</style>
